import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, IconButton } from '@material-ui/core';
import { getIn, useFormikContext } from 'formik';
import { lookupsJson } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';

import useSession from 'core/hooks/use-session';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import pencilIcon from 'core/assets/svg/pencil-orange.svg';
import checkIcon from 'core/assets/svg/check-green.svg';
import closeIcon from 'core/assets/svg/x.svg';
import useStyles from './protection-class-override.styles';

// Farm fire-protection-class option list: both `id` (stored, consumed by the rater) and `value`
// (display label) are the ISO class (1..10, 8B). Keeping one list means the override can only produce
// a class the raters already accept (#35212; ISO display corrected in #35388).
const fpcOptions = lookupsJson.farm.fireProtectionClassOptions;

/**
 * Underwriter protection-class (fire protection class / fpc) override (issue #35212).
 *
 * Renders the property's fire-protection class and, for an underwriter on a mutual brand that allows
 * it, a pencil next to the value — mirroring the insurance-score override. Clicking the pencil turns
 * the value into an inline select (valid protection classes) plus a required reason. Saving marks the
 * property's `fpcOverride` boolean true and stores the chosen class, the reason, and who applied it
 * (`fpcOverrideBy`, from the session; the backend re-stamps it from the trusted identity). Clearing
 * the selection and saving removes the override, reverting to the system-computed value on the next
 * rate. The override persists (rating + display) until it is cleared or the protection class is
 * re-run. The backend independently gates writes on the underwriter permission, so these gates are
 * display-only.
 *
 * Gated by the `canApproveUWReview` permission AND the brand `featureFlags.protectionClassOverride`
 * flag (on for the mutual brands — CFM, Forreston, Oakwood; off for Branch).
 */
const ProtectionClassOverride = ({ fieldName, computedFpc, isNA, label, xs, disabled }) => {
  const classes = useStyles();
  const { canApproveUWReview, user } = useSession();
  const { values, setFieldValue } = useFormikContext();
  const [editing, setEditing] = useState(false);
  const priorRef = useRef({ isOverriding: false, overrideValue: null, overrideReason: null, overrideBy: null });

  const overridePath = `${fieldName}.fpcOverride`;
  const valuePath = `${fieldName}.fpcOverrideValue`;
  const reasonPath = `${fieldName}.fpcOverrideReason`;
  const byPath = `${fieldName}.fpcOverrideBy`;

  const isOverriding = !!getIn(values, overridePath);
  const overrideValue = getIn(values, valuePath);
  const overrideReason = getIn(values, reasonPath);
  const overrideBy = getIn(values, byPath);

  // `disabled` mirrors the gating on the sibling location fields (isLocationFormDisabled): the tab-wide
  // DisableContext, or another location's form being actively edited. When disabled we show the value
  // read-only (no pencil) even for an underwriter.
  const canOverride = Boolean(canApproveUWReview && appConfig?.featureFlags?.protectionClassOverride && !disabled);
  const displayed = isNA ? 'N/A' : resolveDisplayedFpc(isOverriding, overrideValue, computedFpc);

  const currentValue = getIn(values, valuePath);
  const currentReason = getIn(values, reasonPath);
  const hasSelection = currentValue !== '' && currentValue !== null && currentValue !== undefined;
  const hasReason = typeof currentReason === 'string' && currentReason.trim() !== '';
  // A reason is required when actually applying an override; clearing (no selection) needs no reason.
  const saveDisabled = hasSelection && !hasReason;

  const startEdit = () => {
    priorRef.current = { isOverriding, overrideValue, overrideReason, overrideBy };
    // Seed the select with the value currently shown so the underwriter edits it in place.
    if (!isOverriding) {
      setFieldValue(valuePath, computedFpc ?? '');
    }
    setEditing(true);
  };

  const save = () => {
    if (saveDisabled) {
      return;
    }
    if (!hasSelection) {
      // Cleared → remove the override and fall back to the system-computed value.
      setFieldValue(overridePath, false);
      setFieldValue(valuePath, null);
      setFieldValue(reasonPath, null);
      setFieldValue(byPath, null);
    } else {
      setFieldValue(overridePath, true);
      setFieldValue(valuePath, currentValue);
      setFieldValue(reasonPath, currentReason.trim());
      setFieldValue(byPath, user?.email ?? null);
    }
    setEditing(false);
  };

  const cancel = () => {
    const prior = priorRef.current;
    setFieldValue(overridePath, prior.isOverriding);
    setFieldValue(valuePath, prior.isOverriding ? prior.overrideValue : null);
    setFieldValue(reasonPath, prior.isOverriding ? prior.overrideReason : null);
    setFieldValue(byPath, prior.isOverriding ? prior.overrideBy : null);
    setEditing(false);
  };

  if (editing) {
    return (
      <Grid item xs={xs} className={classes.field}>
        {label && (
          <Label type="infoLabel" className={classes.fieldLabel}>
            {label}
          </Label>
        )}
        <Grid container direction="column" className={classes.editRow}>
          <Grid container alignItems="center" wrap="nowrap">
            <div className={classes.selectWrap}>
              <FormField id={valuePath} name={valuePath} type="select" mode="light" options={fpcOptions} optional />
            </div>
            <IconButton
              className={classes.iconButton}
              onClick={save}
              disabled={saveDisabled}
              aria-label="Save protection class override"
            >
              <img className={classes.icon} src={checkIcon} alt="Save" />
            </IconButton>
            <IconButton className={classes.iconButton} onClick={cancel} aria-label="Cancel protection class override">
              <img className={classes.icon} src={closeIcon} alt="Cancel" />
            </IconButton>
          </Grid>
          <div className={classes.reasonWrap}>
            <FormField id={reasonPath} name={reasonPath} type="string" mode="light" placeholder="Reason for override" />
          </div>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid item xs={xs} className={classes.field}>
      {label && (
        <Label type="infoLabel" className={classes.fieldLabel}>
          {label}
        </Label>
      )}
      <Grid container alignItems="center" wrap="nowrap">
        <Label type="infoValue">{displayed}</Label>
        {canOverride && !isNA && (
          <IconButton
            className={classes.iconButton}
            onClick={startEdit}
            aria-label="Override protection class"
            title={isOverriding ? 'Manually overridden — edit' : 'Override protection class'}
          >
            <img className={classes.icon} src={pencilIcon} alt="Edit" />
          </IconButton>
        )}
      </Grid>
      {isOverriding && (
        <Label type="infoLabel" className={classes.note}>
          {`Overridden by ${overrideBy || 'underwriter'}${overrideReason ? `: ${overrideReason}` : ''}`}
        </Label>
      )}
    </Grid>
  );
};

ProtectionClassOverride.propTypes = {
  fieldName: PropTypes.string.isRequired,
  computedFpc: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isNA: PropTypes.bool,
  label: PropTypes.string,
  xs: PropTypes.number,
  disabled: PropTypes.bool
};

ProtectionClassOverride.defaultProps = {
  computedFpc: null,
  isNA: false,
  label: 'Fire Protection Class',
  xs: 4,
  disabled: false
};

/**
 * The fire-protection class to display: the manual override value when the override is active with a
 * non-empty class, otherwise the system-computed value. Mirrors the rater's isFpcOverridden.
 */
export const resolveDisplayedFpc = (isOverriding, overrideValue, computedFpc) =>
  isOverriding && typeof overrideValue === 'string' && overrideValue.trim() !== ''
    ? overrideValue
    : computedFpc || 'N/A';

export default ProtectionClassOverride;
